import { z } from 'zod';

export const INDUSTRIES = [
  'restaurant',
  'retail',
  'local_services',
  'healthcare',
  'education',
  'real_estate',
  'other',
] as const;

export const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'linkedin', 'youtube', 'x', 'other'] as const;

const INDIAN_PHONE_PATTERN = /^(\+91)?[6-9]\d{9}$/;

/** 10-digit Indian mobile, optional +91 prefix. Shared by the onboarding phone step and the profile API. */
export const contactPhoneSchema = z.string().regex(INDIAN_PHONE_PATTERN, 'Enter a valid 10-digit Indian mobile number');

export const socialLinkSchema = z.object({
  platform: z.enum(SOCIAL_PLATFORMS),
  url: z.url(),
});

export const businessProfileInputSchema = z
  .object({
    industry: z.enum(INDUSTRIES),
    customIndustry: z.string().trim().min(1).max(100).optional(),
    businessName: z.string().trim().min(1).max(255),
    googlePlaceId: z.string().min(1).max(255).optional(),
    websiteUrl: z.union([z.url(), z.literal('')]).optional(),
    socialLinks: z.array(socialLinkSchema).max(10).default([]),
    contactPhone: contactPhoneSchema,
  })
  .refine((input) => input.industry !== 'other' || Boolean(input.customIndustry), {
    message: 'customIndustry is required when industry is "other"',
    path: ['customIndustry'],
  });

export type BusinessProfileInput = z.infer<typeof businessProfileInputSchema>;
export type SocialLink = z.infer<typeof socialLinkSchema>;

/** Shape returned by GET /api/business/profile (read model; differs from the POST input). */
export interface BusinessProfileResponse {
  businessName: string;
  businessType: string;
  primaryCategory: string;
  websiteUrl: string;
  contactPhone: string;
  googlePlaceId: string | null;
  addressLine1: string | null;
  socialLinks: { platform: string; url: string }[];
}

export type BusinessProfileFormValues = z.input<typeof businessProfileInputSchema>;

const isIndustry = (v: string): v is (typeof INDUSTRIES)[number] => (INDUSTRIES as readonly string[]).includes(v);
const isPlatform = (v: string): v is (typeof SOCIAL_PLATFORMS)[number] =>
  (SOCIAL_PLATFORMS as readonly string[]).includes(v);

/**
 * Maps the GET read model back into POST-shaped form values so a settings page can edit and re-save.
 * `googlePlaceId` MUST be sent back unchanged: the server re-derives the stored address/maps link
 * from it on every save, so omitting it would wipe them. `addressLine1` is intentionally not part of
 * the form values (it is server-derived and read-only).
 */
export function profileToFormValues(profile: BusinessProfileResponse): BusinessProfileFormValues {
  const industry = isIndustry(profile.primaryCategory) ? profile.primaryCategory : 'other';
  return {
    industry,
    // For 'other' the server stores the free-text industry in businessType.
    ...(industry === 'other' ? { customIndustry: profile.businessType } : {}),
    businessName: profile.businessName,
    ...(profile.googlePlaceId ? { googlePlaceId: profile.googlePlaceId } : {}),
    websiteUrl: profile.websiteUrl,
    socialLinks: profile.socialLinks.map((l) => ({
      platform: isPlatform(l.platform) ? l.platform : 'other',
      url: l.url,
    })),
    contactPhone: profile.contactPhone,
  };
}

/**
 * Settings → Profile & business edits the account holder's name and the business profile in one form.
 * (Intersection rather than `.extend`: the business schema carries a refinement.)
 */
export const profileSettingsFormSchema = businessProfileInputSchema.and(
  z.object({ fullName: z.string().trim().min(1, 'Enter your name').max(255) }),
);

export type ProfileSettingsFormValues = z.input<typeof profileSettingsFormSchema>;

export function profileToSettingsValues(profile: BusinessProfileResponse, fullName: string): ProfileSettingsFormValues {
  return { ...profileToFormValues(profile), fullName };
}

/** Splits settings form values back into the part for `auth.updateUser` and the part for `POST /api/business/profile`. */
export function splitSettingsValues(values: ProfileSettingsFormValues): {
  fullName: string;
  business: BusinessProfileFormValues;
} {
  const { fullName, ...business } = values;
  return { fullName, business };
}
